import { StoreBase } from '@tk/basics/tk.store.base';
import { IDBPDatabase, openDB, StoreNames } from 'idb';

export enum CompareActions {
    SWITCH_CATEGORY = 'switchCategory',
    REMOVE_CATEGORY = 'removeCategory',
    ADD_MULTIPLE_ITEMS = 'addMultipleItems',
    PATCH_ITEM = 'patchItem',
    CLEAR_TABLE = 'clearTable',
    CLEAR_ALL_TABLE = 'clearAllTable',
}

export interface Article {
    id: number;
    name: string;
    attributes: Record<string, string>;
    tileHTML?: string;
    tileHeaderHTML?: string;
    createdAt?: number;
}

export interface Category {
    id: number;
    name: string;
    articleIds: number[];
}

export interface AttributeResponse {
    id: string;
    categoryId: string;
    attribute: string;
    label: string;
    groupId: number;
    groupName: string;
    isImportant: boolean;
    unit: string;
    unitName: string;
}

export interface CompareStore {
    categories: {
        key: number;
        value: Category;
    };
    articles: {
        key: number;
        value: Article;
    };
    attributeList: {
        key: string;
        value: AttributeResponse;
    };
}

export interface CompareState {
    categories: Category[];
    articles: Article[];
    attributeList: AttributeResponse[];
    currentCategoryId: number;
}

export interface CompareReducer {
    state: CompareState;
    action: CompareActions;
}

export default class TKCompareStore extends StoreBase<CompareState> {
    database?: IDBPDatabase<CompareStore>;

    constructor() {
        super({
            categories: [],
            articles: [],
            attributeList: [],
            currentCategoryId: 0,
        });
    }

    async initialize() {
        this.database = await openDB('CompareStore', 1, {
            upgrade(db) {
                db.createObjectStore('categories');
                db.createObjectStore('articles');
                db.createObjectStore('attributeList').createIndex('categoryId', 'categoryId', { unique: false });
            },
        });
        const firstCategory = (await this.database.getAll('categories')).at(0) as Category;
        if (firstCategory) this.state.currentCategoryId = firstCategory.id;
        await this.saveAllStores();
    }

    getState(): CompareState {
        return this.state;
    }

    async clearAll() {
        await this.database?.clear('categories');
        await this.database?.clear('articles');
        await this.database?.clear('attributeList');
        await this.syncState();
        this.notify(CompareActions.CLEAR_ALL_TABLE, this.state);
    }

    async clear(storeName: StoreNames<CompareStore>) {
        await this.database?.clear(storeName);
        await this.syncState();
        this.notify(CompareActions.CLEAR_TABLE, this.state);
    }

    getCategoryById(categoryId: number) {
        const { categories } = this.state;
        return categories.find((item) => item.id === categoryId);
    }

    getArticleCountByCategoryId(categoryId: number) {
        const { categories, articles } = this.state;
        const category = categories.find((item) => item.id === categoryId);
        const length = articles.filter((article) => category?.articleIds.includes(article.id)).length;
        if (length === 0) return category?.articleIds.length;
        else return length;
    }

    async patchCategoryById(categoryId: number, category: Category) {
        await this.database?.put('categories', category, categoryId);
        await this.syncState();
        this.notify(CompareActions.PATCH_ITEM, this.state);
    }

    async removeCategoryById(categoryId: number) {
        await this.database?.delete('categories', categoryId);
        await this.syncState();
        this.notify(CompareActions.REMOVE_CATEGORY, this.state);
    }

    async getEntriesByCategoryId(categoryId: number) {
        if (!this.database) return;
        const tx = this.database.transaction('attributeList', 'readonly');
        const index = tx.store.index('categoryId');
        const keyRange = IDBKeyRange.only(categoryId);
        const results = await index.getAll(keyRange);
        await tx.done;
        return results as AttributeResponse[];
    }

    async addMultipleItems<T extends { id: string | number }>(storeName: StoreNames<CompareStore>, items: T[]) {
        const transaction = this.database?.transaction(storeName, 'readwrite');
        const store = transaction?.objectStore(storeName);

        if (!this.hasAllItemsAnId(items)) return;

        await Promise.all([
            items.map(async (item) => {
                await store?.put(item, item.id);
            }),
        ]);
        await transaction?.done;
        await this.syncState();
        this.notify(CompareActions.ADD_MULTIPLE_ITEMS, this.state);
    }

    async modifyItem(storeName: StoreNames<CompareStore>, id: number, modifiedState: NonNullable<unknown>) {
        const item = await this.database?.get(storeName, id);
        const newItem = { ...item, ...modifiedState };
        await this.database?.put(storeName, newItem, id);
        await this.syncState();
        this.notify(CompareActions.PATCH_ITEM, this.state);
    }

    switchCategory(categoryId: number) {
        this.state.currentCategoryId = categoryId;
        this.notify(CompareActions.SWITCH_CATEGORY, this.state);
    }

    protected async syncState() {
        await this.saveAllStores();
    }

    protected async saveAllStores() {
        this.state = {
            categories: (await this.database?.getAll('categories')) as Category[],
            articles: (await this.database?.getAll('articles')) as Article[],
            attributeList: (await this.database?.getAll('attributeList')) as AttributeResponse[],
            currentCategoryId: this.state.currentCategoryId,
        };
    }

    protected hasAllItemsAnId<T extends { id: string | number }>(items: T[]): boolean {
        const invalidItems = items.filter((item) => !('id' in item) || item.id === undefined);
        return invalidItems.length === 0;
    }
}
